import { useState } from "react";
import { format } from "date-fns";
import { useInterviews } from "@/hooks/useInterviews";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface RescheduleInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interviewId: string;
  currentDateTime: Date;
  onSuccess?: () => void;
}

export function RescheduleInterviewDialog({
  open,
  onOpenChange,
  interviewId,
  currentDateTime,
  onSuccess
}: RescheduleInterviewDialogProps) {
  const { updateInterview } = useInterviews();
  const [date, setDate] = useState<Date>(currentDateTime);
  const [time, setTime] = useState<string>(
    format(currentDateTime, "HH:mm")
  );
  const [reason, setReason] = useState("");

  const handleReschedule = () => {
    const [hours, minutes] = time.split(':');
    const newDateTime = new Date(date);
    newDateTime.setHours(parseInt(hours), parseInt(minutes));

    updateInterview.mutate(
      {
        id: interviewId,
        updates: {
          scheduled_at: newDateTime.toISOString(),
          notes: reason ? `Rescheduled: ${reason}` : undefined
        }
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onOpenChange(false);
        }
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule Interview</DialogTitle>
          <DialogDescription>
            Choose a new date and time for this interview.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>New Date</Label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={(date) => date < new Date()}
              className="rounded-md border"
            />
          </div>
          
          <div>
            <Label>New Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
          
          <div>
            <Label>Reason (Optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you rescheduling?"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleReschedule} disabled={updateInterview.isPending}>
            {updateInterview.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reschedule Interview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
